import { CoverageItem } from '../../utils/insurance-score';

export type Uuid = string;
/** ISO-8601 timestamp, e.g. "2026-06-14T10:15:30Z". */
export type IsoDateTime = string;

export type AnalysisStatus = 'PENDING' | 'RUNNING' | 'COMPLETE' | 'FAILED' | 'SKIPPED' | 'CANCELLED';
export type GapSeverity = 'CRITICAL' | 'MEDIUM' | 'LOW';
export type DinNeedLevel = 'STUFE_1' | 'STUFE_2' | 'STUFE_3';
export type CostInterval = 'MONTHLY' | 'YEARLY';
export type ContractRecommendation =
  | 'SUFFICIENT' // Ausreichend
  | 'CHECK' // Prüfen
  | 'INSUFFICIENT' // Unzureichend
  | 'UPGRADE' // Optimieren
  | 'CANCEL'; // Kündigen

/** Backend error codes returned in the response body for AI-gated failures. */
export type AiErrorCode = 'AI_QUOTA_EXCEEDED';

// ---- Request bodies ----

export interface BulkAnalysisRequest {
  /** Client (Keycloak) ids to analyse. 1..1000. */
  clientIds: Uuid[];
}

/** Body for sharing the report with the client as a chat message. */
export interface ContractAiShareReportRequest {
  message: string;
}

/** Response of the share-via-chat endpoint. */
export interface ContractAiShareReportResponse {
  messageId: number;
}

// ---- Response bodies ----

/** One identified coverage gap ("Fehlende Absicherung"). */
export interface CoverageGap {
  insuranceType: string; // "Berufsunfähigkeitsversicherung"
  category: string; // "Personenversicherung"
  subCategory: string; // "Arbeitskraft"
  riskLabel: string; // "Einkommensausfall durch Berufsunfähigkeit"
  severity: GapSeverity; // CRITICAL -> "Kritisch", MEDIUM -> "Mittel", LOW -> "Niedrig"
  dinLevel: DinNeedLevel;
  dinTopicRank: number | null;
  justification: string; // "BEGRÜNDUNG (DIN SPEC 77222)"
  recommendation: string; // "EMPFEHLUNG"
  estimatedCostMin: number | null; // e.g. 150  (EUR)
  estimatedCostMax: number | null; // e.g. 220
  costInterval: CostInterval; // "€/Monat" | "€/Jahr"
  displayOrder: number | null;
  // reserved for a future deterministic engine — currently null:
  targetSum: number | null;
  actualSum: number | null;
  topicDegreePercent: number | null;
}

/**
 * Per-contract verdict. Only the contract's uid + verdict are stored by the backend —
 * join this onto your existing contract list (from /b2b/contracts) by `contractUid`
 * to render the Status column. The contract's provider/amount/number come from YOUR data.
 */
export interface ContractAssessment {
  contractUid: Uuid;
  statusRecommendation: ContractRecommendation;
  note: string | null;
  displayOrder: number | null;
}

/** Full analysis result — drives the whole Analyse tab. */
export interface ContractAnalysis {
  analysisUid: Uuid;
  advisorId: Uuid;
  clientId: Uuid;
  status: AnalysisStatus; // "AI Scan Status" — COMPLETE == "Komplett"
  specReference: string; // "DIN SPEC 77222"
  // KPI tiles:
  analyzedContracts: number | null; // "Analysierte Verträge: 7"
  distinctProviders: number | null; // "Über 4 Anbieter"
  coverageGapCount: number | null; // "Deckungslücken: 3"
  coverageDegreePercent: number | null; // "Deckungsgrad: 62"
  recommendedDegreePercent: number | null; // "Empfohlen: 85"
  criticalCount: number | null; // "2 Kritisch"
  mediumCount: number | null; // "1 Mittel"
  lowCount: number | null;
  summaryText: string | null; // overall explanation
  /** Advisor-supplied context ("smart prompt") that was injected into this re-run, if any. */
  appliedContext?: string;
  gaps: CoverageGap[];
  /**
   * Full relevant-coverage list (present AND missing, each with priority) — drives the
   * Versicherungs-Score. Not delivered by the backend yet; until it ships, the frontend
   * synthesises a provisional list from the gaps and the analyzed contracts.
   */
  coverageItems?: CoverageItem[];
  contractAssessments: ContractAssessment[];
  schemaVersion: number;
  errorMessage: string | null; // set when status === 'FAILED'
  createdAt: IsoDateTime;
  completedAt: IsoDateTime | null; // "Zuletzt gescannt"
  sharedWithClient: boolean; // advisor toggled visibility for the end client
}

/**
 * Payload of the `ai/analysis-complete` WebSocket action — pushed to the advisor when a single
 * client analysis finishes (COMPLETE or FAILED). Carries only identifiers + status; fetch the full
 * result with {@link SmartAnalysisService.getById} keyed by `analysisUid`.
 */
export interface AnalysisCompleteEvent {
  analysisUid: Uuid;
  clientId: Uuid;
  status: AnalysisStatus;
}

/** Advisor's AI token balance — drives the header usage pill (view model). */
export interface AiUsage {
  used: number; // lifetime tokens consumed, e.g. 23824
  limit: number; // lifetime tokens granted (free starter + purchases), e.g. 1000000
  planTier: string; // cosmetic plan label shown next to the bar, e.g. "REGULAR"
}

/** Raw payload from `GET /ai/usage/summary` — mapped to {@link AiUsage} in the service. */
export interface AiUsageSummary {
  month: string; // "2026-06"
  plan: string; // "REGULAR"
  usedTokens: number; // 23824
  quotaTokens: number; // 1000000
  remainingTokens: number; // 976176
}

/**
 * AI usage is metered in tokens on the backend (that is what the model reports), but shown to the
 * advisor as "credits": 1 credit = 1.000 tokens (kept in sync with the ai service's tokens-per-credit
 * and its wallet SQL). Conversion is display-only.
 */
export const TOKENS_PER_CREDIT = 1000;

/** Tokens → credits for display (rounded to the nearest whole credit). */
export function tokensToCredits(tokens: number): number {
  return Math.round((tokens ?? 0) / TOKENS_PER_CREDIT);
}

/** The one-time AI token package + the saved card it would be charged to (from `GET /account/ai-tokens/product`). */
export interface AiTokenProduct {
  productId: string;
  displayName: string; // "AI Tokens"
  description: string | null;
  tokens: number; // permanent tokens granted by one purchase, e.g. 1250000 (= 1.250 credits)
  amount: number; // price in the smallest currency unit (cents), e.g. 1900
  currency: string; // ISO currency, e.g. "eur"
  hasPaymentMethod: boolean; // whether a saved default card exists
  cardBrand: string | null; // e.g. "visa"
  cardLast4: string | null; // e.g. "4242"
}

/** Outcome of `POST /account/ai-tokens/purchase`. */
export interface AiTokenPurchaseResult {
  /** SUCCEEDED: charged off-session, tokens credited. REDIRECT: open `checkoutUrl` to finish (3-DS / no card). */
  status: 'SUCCEEDED' | 'REDIRECT';
  tokens: number;
  checkoutUrl: string | null;
}

/** Bulk-run progress handle. */
export interface AnalysisBatch {
  batchUid: Uuid;
  advisorId: Uuid;
  status: AnalysisStatus; // RUNNING | COMPLETE | FAILED
  totalClients: number;
  completedCount: number;
  failedCount: number;
  skippedCount: number; // clients never run (quota ran out mid-batch / cancelled)
  createdAt: IsoDateTime;
  completedAt: IsoDateTime | null;
}
